import React from 'react';
import {
  CartesianGrid,
  Line,
  LineChart as BaseLineChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { flatMap } from 'remeda';

import {
  CaptionText,
} from '@make-software/cspr-design';
import { HeaderText, formatDateShort,
  formatNumber, } from '@block-explorer/ui';
import { ChartTooltip } from '../chart-tooltip/chart-tooltip';
import { useTheme } from 'styled-components';
import { parse, set, format } from 'date-fns';
import { useTranslation } from 'react-i18next';

interface Props<T extends Record<string, any>> {
  series: { name: string; data?: T[] | null }[];
  xAxisGetter: (dataItem: T) => number | string;
  yAxisGetter: (dataItem: T) => number | string;
}

const formatDeploysCount = (value): string =>
  `${formatNumber(value, {
    notation: 'compact',
    compactDisplay: 'short',
  })}`;

const formatUTCDate = (dateString: string) => {
  const date = parse(dateString, 'yyyy-MM-dd', new Date());
  const utcDate = set(date, {
    hours: 0,
    minutes: 0,
    seconds: 0,
    milliseconds: 0,
  });
  return format(utcDate, "MMMM d, yyyy, HH:mm 'UTC'");
};

const CustomTooltip = ({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: Array<{ value }>;
  label?: string;
}) => {
  if (active && payload && payload.length) {
    return (
      <ChartTooltip>
        <HeaderText size={5}>{formatDeploysCount(payload[0].value)}</HeaderText>
        <br />
        <CaptionText size={2} variation="darkGray">
          {label && formatUTCDate(label)}
        </CaptionText>
      </ChartTooltip>
    );
  }

  return null;
};

export const DailyDeploysChart = <T extends Record<string, any>>({
  series,
  xAxisGetter,
  yAxisGetter,
}: Props<T>) => {
  const theme = useTheme();
  const { t } = useTranslation();

  const parsedSeries = series.map((s) => ({
    ...s,
    data: s.data?.map((i) => ({
      xAxisValue: xAxisGetter(i),
      yAxisValue: yAxisGetter(i),
    })),
  }));

  const seriesLengthEmpty = flatMap(parsedSeries, (i) => i.data).length === 0;
  if (seriesLengthEmpty) {
    return <>No data.</>;
  }

  const baseLineChartMargin = { top: 20, right: 0, left: 0, bottom: 0 };

  return (
    <ResponsiveContainer width={'100%'} height={180}>
      <BaseLineChart
        margin={baseLineChartMargin}
        style={{ fontSize: 10 }}
        title={t('Transactions past in 14 days chart')}
        desc={t(
          'A dynamic graph showing the number of transactions over the last 14 days.'
        )}
      >
        <CartesianGrid stroke={theme.styleguideColors.borderPrimary} />
        <YAxis
          dataKey="yAxisValue"
          tickFormatter={formatDeploysCount}
          stroke={theme.styleguideColors.contentSecondary}
          axisLine={false}
          tickSize={0}
          tickMargin={12}
          minTickGap={35}
          interval="preserveEnd"
          width={50}
        />
        <XAxis
          dataKey="xAxisValue"
          tickFormatter={(value) => {
            return formatDateShort(value);
          }}
          stroke={theme.styleguideColors.contentSecondary}
          axisLine={false}
          tickSize={0}
          tickMargin={10}
          minTickGap={69}
          dx={8.5}
          interval="preserveEnd"
        />
        {parsedSeries.map((s) => (
          <Line
            key={s.name}
            name={s.name}
            data={s.data}
            type="monotone"
            dataKey="yAxisValue"
            stroke={theme.styleguideColors.contentLightBlue}
            strokeWidth={2}
            isAnimationActive={false}
            dot={false}
          />
        ))}
        <RechartsTooltip content={<CustomTooltip />} />
      </BaseLineChart>
    </ResponsiveContainer>
  );
};
